import {
  resolveCustomerDashboardModeFromTruth,
  type CustomerActionCard,
  type CustomerDashboardMode,
  type CustomerDashboardView,
} from '@solar/contracts';

export interface DashboardHeroContent {
  readonly eyebrow: string;
  readonly title: string;
  readonly description: string;
  readonly ctaLabel: string | null;
  readonly ctaPath: string | null;
  readonly secondaryCtaLabel: string | null;
  readonly secondaryCtaPath: string | null;
  readonly tone: 'default' | 'urgent' | 'success' | 'info';
}

export function getPrimaryRequiredAction(
  actions: readonly CustomerActionCard[] | undefined,
): CustomerActionCard | undefined {
  return actions?.[0];
}

export function getSecondaryRequiredActions(
  actions: readonly CustomerActionCard[] | undefined,
): CustomerActionCard[] {
  if (actions === undefined || actions.length === 0) {
    return [];
  }

  const primary = getPrimaryRequiredAction(actions);

  if (primary === undefined) {
    return [];
  }

  return actions.filter((action) => action.code !== primary.code);
}

export function resolveCustomerDashboardMode(data: CustomerDashboardView): CustomerDashboardMode {
  return (
    data.mode ??
    resolveCustomerDashboardModeFromTruth({
      hasCustomerProfile: data.hasCustomerProfile,
      onboardingComplete: data.onboardingComplete,
      planFullyPaid: data.planFullyPaid,
      applicationStatus: data.applicationStatus,
      mandateStatus: data.mandateStatus,
      nextScheduleStatus: data.nextScheduleStatus,
      requiredActionCodes: (data.requiredActions ?? []).map((action) => action.code),
    })
  );
}

export function resolveDashboardHero(
  data: CustomerDashboardView,
  mode: CustomerDashboardMode,
): DashboardHeroContent {
  const primaryAction = getPrimaryRequiredAction(data.requiredActions);

  if (mode === 'ONBOARDING') {
    return {
      eyebrow: 'Account setup',
      title: 'Continue setting up your account',
      description: 'Finish the guided account setup to unlock solar plan applications.',
      ctaLabel: 'Continue account setup',
      ctaPath: '/app/customer/onboarding',
      secondaryCtaLabel: null,
      secondaryCtaPath: null,
      tone: 'default',
    };
  }

  if (mode === 'NO_APPLICATION') {
    return {
      eyebrow: 'Ready to go solar?',
      title: 'Find the right solar solution',
      description:
        'Explore available solar products and choose a repayment option that works for you.',
      ctaLabel: 'Explore solar products',
      ctaPath: '/app/customer/products',
      secondaryCtaLabel: 'Start application',
      secondaryCtaPath: '/app/customer/apply',
      tone: 'default',
    };
  }

  if (mode === 'APPLICATION_IN_PROGRESS') {
    if (data.applicationStatus === 'DRAFT' || data.applicationStatus === 'CHANGES_REQUESTED') {
      return {
        eyebrow: 'Application in progress',
        title: 'Continue your application',
        description:
          data.changeRequestReason ??
          'Review your product and plan choices, then submit for review.',
        ctaLabel: 'Continue application',
        ctaPath: primaryAction?.actionPath ?? '/app/customer/applications',
        secondaryCtaLabel: null,
        secondaryCtaPath: null,
        tone: data.applicationStatus === 'CHANGES_REQUESTED' ? 'urgent' : 'default',
      };
    }

    if (data.applicationStatus === 'REJECTED') {
      return {
        eyebrow: 'Application update',
        title: 'Application declined',
        description:
          data.rejectionReason ??
          'Your previous application was not approved. You may start a new application.',
        ctaLabel: 'Start new application',
        ctaPath: '/app/customer/apply',
        secondaryCtaLabel: 'View applications',
        secondaryCtaPath: '/app/customer/applications',
        tone: 'info',
      };
    }

    return {
      eyebrow: 'Application submitted',
      title: 'Your application is being reviewed',
      description:
        'We will notify you when there is an update on your solar financing application.',
      ctaLabel: 'View application',
      ctaPath: '/app/customer/applications',
      secondaryCtaLabel: null,
      secondaryCtaPath: null,
      tone: 'info',
    };
  }

  if (mode === 'CONTRACT_SETUP') {
    if (data.applicationStatus === 'AWAITING_CUSTOMER_ACCEPTANCE') {
      return {
        eyebrow: 'Contract ready',
        title: 'Your agreement is ready',
        description: 'Review and accept your approved solar financing contract.',
        ctaLabel: 'Review contract',
        ctaPath: '/app/customer/contracts',
        secondaryCtaLabel: null,
        secondaryCtaPath: null,
        tone: 'default',
      };
    }

    return {
      eyebrow: 'Direct Debit setup',
      title: 'Set up Direct Debit',
      description: 'Complete mandate activation to enable your monthly solar repayments.',
      ctaLabel: 'Set up Direct Debit',
      ctaPath: primaryAction?.actionPath ?? '/app/customer/contracts',
      secondaryCtaLabel: null,
      secondaryCtaPath: null,
      tone: 'default',
    };
  }

  if (mode === 'ATTENTION_REQUIRED') {
    if (data.nextScheduleStatus === 'PROCESSING') {
      return {
        eyebrow: 'Payment update',
        title: 'Your payment is being processed',
        description:
          'We are confirming your instalment with your bank. No action is needed right now.',
        ctaLabel: null,
        ctaPath: null,
        secondaryCtaLabel: 'View repayments',
        secondaryCtaPath: '/app/customer/repayments',
        tone: 'info',
      };
    }

    if (data.nextScheduleStatus === 'FAILED') {
      return {
        eyebrow: 'Payment attention',
        title: 'Payment unsuccessful',
        description:
          'Your last instalment attempt did not complete. Review your repayment schedule.',
        ctaLabel: 'View repayments',
        ctaPath: '/app/customer/repayments',
        secondaryCtaLabel: null,
        secondaryCtaPath: null,
        tone: 'urgent',
      };
    }

    if (primaryAction?.code === 'AUTHORIZE_PAYMENT') {
      return {
        eyebrow: 'Payment requires attention',
        title: `Your instalment of ${data.nextInstalmentAmount ?? '—'} requires authorization`,
        description: primaryAction.description,
        ctaLabel: 'Authorize payment',
        ctaPath: primaryAction.actionPath,
        secondaryCtaLabel: null,
        secondaryCtaPath: null,
        tone: 'urgent',
      };
    }

    if (primaryAction?.code === 'CLEAR_OVERDUE' || data.nextScheduleStatus === 'OVERDUE') {
      return {
        eyebrow: 'Overdue repayment',
        title: 'Your repayment is overdue',
        description: primaryAction?.description ?? 'Clear overdue instalments to stay on track.',
        ctaLabel: 'View repayments',
        ctaPath: '/app/customer/repayments',
        secondaryCtaLabel: null,
        secondaryCtaPath: null,
        tone: 'urgent',
      };
    }

    return {
      eyebrow: 'Action required',
      title: primaryAction?.title ?? 'Something needs your attention',
      description: primaryAction?.description ?? 'Review the required action below.',
      ctaLabel: primaryAction ? actionButtonLabel(primaryAction.code) : null,
      ctaPath: primaryAction?.actionPath ?? null,
      secondaryCtaLabel: null,
      secondaryCtaPath: null,
      tone: 'urgent',
    };
  }

  if (mode === 'COMPLETED') {
    return {
      eyebrow: 'Plan complete',
      title: 'Your solar plan is fully paid',
      description: 'Thank you for completing your solar repayment journey with Seamontech.',
      ctaLabel: 'View payment history',
      ctaPath: '/app/customer/payments',
      secondaryCtaLabel: 'View solar journey',
      secondaryCtaPath: '/app/customer/timeline',
      tone: 'success',
    };
  }

  return {
    eyebrow: 'Solar repayment',
    title: 'Your plan is active',
    description: 'Track upcoming instalments, payments and progress here.',
    ctaLabel: 'View repayments',
    ctaPath: '/app/customer/repayments',
    secondaryCtaLabel: null,
    secondaryCtaPath: null,
    tone: 'default',
  };
}

export function actionButtonLabel(code: string): string {
  switch (code) {
    case 'AUTHORIZE_PAYMENT':
      return 'Authorize payment';
    case 'ACTIVATE_MANDATE':
      return 'Activate mandate';
    case 'ACCEPT_CONTRACT':
      return 'Review contract';
    case 'FINISH_APPLICATION':
      return 'Continue application';
    case 'COMPLETE_ONBOARDING':
      return 'Continue account setup';
    case 'CLEAR_OVERDUE':
      return 'View repayments';
    default:
      return 'Continue';
  }
}

export function shouldShowFinancialStats(mode: CustomerDashboardMode): boolean {
  return mode === 'ACTIVE_REPAYMENT' || mode === 'ATTENTION_REQUIRED' || mode === 'COMPLETED';
}

export function shouldShowRepaymentPanels(mode: CustomerDashboardMode): boolean {
  return shouldShowFinancialStats(mode);
}

export function shouldShowOnboardingSteps(_mode: CustomerDashboardMode): boolean {
  return false;
}

export function shouldShowApplicationJourney(mode: CustomerDashboardMode): boolean {
  return (
    mode === 'APPLICATION_IN_PROGRESS' ||
    mode === 'CONTRACT_SETUP' ||
    mode === 'ACTIVE_REPAYMENT' ||
    mode === 'COMPLETED'
  );
}
